import { useState } from 'react';
import axios from 'axios';
import { useMMAuth } from '@machinemetrics/mm-react-tools';

// "Clear the board and dispatch again" — the UI for POST /sim/dispatch-reset.
//
// Deliberately NOT the same control as Factory Reset in Settings: that one deletes every work order
// including completed history. This only takes undispatched work back off the floor and returns those
// orders to PLANNED, so iterating on a plan stops being destructive. The copy says so, because the two
// are one click apart in a demo and the difference is unrecoverable in one direction.
//
// Skip-and-report is surfaced, not swallowed: orders with started work are listed back with their
// completed quantities. A reset that silently did less than it claimed would be worse than one that
// refused outright.
export default function DispatchResetButton({ companySlug, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState(null);

  const auth  = useMMAuth();
  const token = auth?.accessToken ?? null;

  const run = async () => {
    setConfirming(false);
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(
        '/machinesim/api/sim/dispatch-reset',
        { companies: [companySlug] },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      );
      const r = (res.data?.results || [])[0] || {};
      if (r.error) setError(r.error);
      else setResult(r);
      onDone?.(r);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Dispatch reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button
        onClick={() => setConfirming(true)}
        disabled={busy}
        title="Take undispatched work back off the floor for this company"
        style={{
          background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.35)',
          borderRadius: 4, padding: '6px 12px', fontSize: 12, fontFamily: 'inherit',
          color: busy ? 'var(--cn-dimmed)' : 'var(--cn-text)',
          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap',
        }}
      >
        {busy ? 'Resetting…' : '⟳ Reset dispatch'}
      </button>

      {result && (
        <span style={{ fontSize: 12, color: 'var(--cn-text)' }}>
          <strong>{result.clearedCount}</strong> cleared
          {result.skippedCount > 0 && (
            <>
              {' · '}
              <span
                title={result.skipped.map(s => `${s.woNumber}: ${s.reason}`).join('\n')}
                style={{ color: 'var(--cn-muted)', borderBottom: '1px dotted var(--cn-muted)', cursor: 'help' }}
              >
                {result.skippedCount} skipped (already started)
              </span>
            </>
          )}
          {result.released?.length > 0 && ` · ${result.released.length} machine(s) released`}
        </span>
      )}
      {error && <span style={{ fontSize: 12, color: 'var(--cn-danger)' }}>{error}</span>}

      {confirming && (
        <div
          onClick={() => setConfirming(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.25)',
              borderRadius: 8, padding: 22, maxWidth: 460, boxShadow: '0 18px 50px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--cn-text)', marginBottom: 10 }}>
              Reset dispatch for “{companySlug}”?
            </div>
            <p style={{ fontSize: 13, color: 'var(--cn-muted)', lineHeight: 1.55, margin: '0 0 8px' }}>
              Takes undispatched work back off the floor and returns those orders to <strong>PLANNED</strong>,
              so they can be dispatched again.
            </p>
            <p style={{ fontSize: 13, color: 'var(--cn-muted)', lineHeight: 1.55, margin: '0 0 18px' }}>
              Orders that have <strong>already started</strong> are left running and reported back.
              Nothing is deleted — this is not a factory reset.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirming(false)}
                style={{
                  background: 'transparent', border: '1px solid rgba(168,144,96,0.25)', borderRadius: 4,
                  padding: '7px 14px', fontSize: 12, fontFamily: 'inherit', color: 'var(--cn-muted)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={run}
                style={{
                  background: 'var(--cn-gold)', border: '1px solid var(--cn-gold)', borderRadius: 4,
                  padding: '7px 14px', fontSize: 12, fontFamily: 'inherit', fontWeight: 700,
                  color: '#1a1408', cursor: 'pointer',
                }}
              >
                Reset dispatch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
